import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Card,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useAppSelector, useAppDispatch } from "@hooks/redux";
import {
  createHive,
  fetchApiariesAndGroups,
  setApiaryEdited,
} from "@store/slices/locationsSlice";
import SaveToolbar from "@components/common/SaveToolbar";
import UnsavedChangesDialog from "@components/common/UnsavedChangesDialog";
import { useUnsavedChanges } from "@hooks/useUnsavedChanges";
import { apiClient } from "@api/index";
import HiveFormFields, {
  HiveFormValues,
} from "@components/hives/HiveFormFields";

interface HiveType {
  id: number;
  name: string;
  type: string;
  description: string;
}

const HiveCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { apiaries, loadingState, error } = useAppSelector(
    (state) => state.locations,
  );
  const [showLoadingIcon, setShowLoadingIcon] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hiveColor, setHiveColor] = useState("#F8B133");
  const [colorTouched, setColorTouched] = useState(false);
  const [hiveTypes, setHiveTypes] = useState<HiveType[]>([]);
  const [loadingHiveTypes, setLoadingHiveTypes] = useState(true);
  const [beeRaces, setBeeRaces] = useState<{ value: string; label: string }[]>([]);
  const [loadingBeeRaces, setLoadingBeeRaces] = useState(true);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<HiveFormValues>({
    defaultValues: {
      name: "",
      location_id: location.state?.apiaryId || null,
      hive_type: 1,
      color: "#F8B133",
      bee_race: "african",
      order: 1,
      label: "",
      notes: "",
    },
  });

  const selectedLocationId = watch("location_id");

  useEffect(() => {
    if (!selectedLocationId && apiaries.length === 1) {
      setValue("location_id", apiaries[0].id);
    }
  }, [apiaries, selectedLocationId, setValue]);

  useEffect(() => {
    if (!selectedLocationId || colorTouched) return;
    const apiary = apiaries.find((item) => item.id === selectedLocationId);
    if (apiary?.hex_color) {
      setHiveColor(apiary.hex_color);
      setValue("color", apiary.hex_color);
    }
  }, [selectedLocationId, apiaries, colorTouched, setValue]);

  // Track form changes
  useEffect(() => {
    if (isDirty) {
      dispatch(setApiaryEdited(true));
    }
  }, [isDirty, dispatch]);

  // Navigation guard
  const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    {
      when: isDirty && !showLoadingIcon,
      message: "You have unsaved changes. Are you sure you want to leave?",
      onConfirm: () => {
        dispatch(setApiaryEdited(false));
      },
    },
  );

  useEffect(() => {
    if (loadingState === "idle") {
      dispatch(fetchApiariesAndGroups());
    }
  }, [dispatch, loadingState]);

  useEffect(() => {
    const fetchHiveTypes = async () => {
      try {
        setLoadingHiveTypes(true);
        const response = await apiClient.get("/hive-types/");
        if (response.data.success) {
          setHiveTypes(response.data.data);
        } else {
          // Fallback to default hive types if API fails
          setHiveTypes([
            {
              id: 1,
              name: "Langstroth",
              type: "langstroth",
              description: "Standard Langstroth hive",
            },
            {
              id: 2,
              name: "Kenya Top Bar Hive",
              type: "top_bar",
              description: "Kenya Top Bar Hive",
            },
            {
              id: 3,
              name: "Dadant",
              type: "dadant",
              description: "Dadant hive",
            },
            {
              id: 4,
              name: "Log Hive",
              type: "log",
              description: "Traditional log hive",
            },
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch hive types:", error);
        // Fallback to default hive types
        setHiveTypes([
          {
            id: 1,
            name: "Langstroth",
            type: "langstroth",
            description: "Standard Langstroth hive",
          },
          {
            id: 2,
            name: "Kenya Top Bar Hive",
            type: "top_bar",
            description: "Kenya Top Bar Hive",
          },
          { id: 3, name: "Dadant", type: "dadant", description: "Dadant hive" },
          {
            id: 4,
            name: "Log Hive",
            type: "log",
            description: "Traditional log hive",
          },
        ]);
      } finally {
        setLoadingHiveTypes(false);
      }
    };

    fetchHiveTypes();
  }, []);

  useEffect(() => {
    const fetchBeeRaces = async () => {
      try {
        setLoadingBeeRaces(true);
        const response = await apiClient.get("/hives/bee_races/");
        if (response.data.success) {
          setBeeRaces(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch bee races:", error);
        // Fallback to default bee races
        setBeeRaces([
          { value: "african", label: "African (Apis mellifera scutellata)" },
          { value: "italian", label: "Italian (Apis mellifera ligustica)" },
          { value: "carnica", label: "Carnica (Apis mellifera carnica)" },
          { value: "buckfast", label: "Buckfast" },
        ]);
      } finally {
        setLoadingBeeRaces(false);
      }
    };

    fetchBeeRaces();
  }, []);

  const handleSaveHive = async (data: HiveFormValues) => {
    if (!data.location_id) {
      setErrorMessage("Please select an apiary");
      return;
    }

    setShowLoadingIcon(true);
    setErrorMessage("");

    const apiary = apiaries.find((item) => item.id === data.location_id);
    const nextOrder = apiary?.hives?.length
      ? Math.max(0, ...apiary.hives.map((hive) => hive.order || 0)) + 1
      : 1;

    try {
      const hiveData: any = {
        name: data.name,
        location_id: data.location_id,
        hive_type: data.hive_type,
        color: data.color,
        bee_race: data.bee_race,
        order: nextOrder,
        label: data.label,
        notes: data.notes,
      };

      await dispatch(createHive(hiveData)).unwrap();
      dispatch(setApiaryEdited(false));
      navigate("/");
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to create hive");
    } finally {
      setShowLoadingIcon(false);
    }
  };

  const handleColorChange = (color: string) => {
    setColorTouched(true);
    setHiveColor(color);
    setValue("color", color, { shouldDirty: true });
  };

  if (loadingState === "pending") {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    );
  }

  const formValues = watch();
  const isValid = !errors.name && !!formValues.name && !!formValues.location_id;

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
      {/* Save Toolbar */}
      <SaveToolbar
        onSave={handleSubmit(handleSaveHive)}
        saveDisabled={!isValid || showLoadingIcon || apiaries.length === 0}
        loading={showLoadingIcon}
        showDelete={false}
      />

      <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
        New Hive
      </Typography>

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setErrorMessage("")}
        >
          {errorMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* No Apiaries Warning */}
      {apiaries.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1">
            No apiaries available yet.{" "}
            <Button
              color="inherit"
              onClick={() => navigate("/apiaries/create")}
              sx={{ textDecoration: "underline" }}
            >
              Create your first apiary
            </Button>
          </Typography>
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(handleSaveHive)}>
            <HiveFormFields
              control={control}
              errors={errors}
              apiaries={apiaries}
              hiveTypes={hiveTypes}
              beeRaces={beeRaces}
              loadingHiveTypes={loadingHiveTypes}
              loadingBeeRaces={loadingBeeRaces}
              hiveColor={hiveColor}
              onColorChange={handleColorChange}
            />
          </form>
        </Box>
      </Card>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </Container>
  );
};

export default HiveCreate;
